import { useCallback, useEffect, useRef, useState } from 'react'
import { getMyWorkspaces } from '@/features/onboarding/services/workspaceApi'
import {
  getDashboardSnapshot,
  regenerateBrief as regenerateBriefRequest,
  resolveApproval as resolveApprovalRequest,
  streamDashboard,
} from '../services/dashboardApi'
import { getCachedDashboard, setCachedDashboard } from '../dashboardSnapshotCache'
import type { DashboardSnapshot, WorkspaceRole } from '../types'

// Flat backoff, not exponential — a dropped dashboard stream is a background
// refresh concern, not something worth hammering a down backend over, but also not
// worth an elaborate retry curve for. The backend's stream always sends a full
// snapshot as its first event on (re)connect, so no separate re-fetch is needed
// here — just reopening the stream is enough to catch up.
const RECONNECT_DELAY_MS = 3000

export type DashboardRefreshResult = { ok: true } | { ok: false; reason: string }

// The manual refresh button's own request can 200 while HubSpot itself is the
// thing that's actually broken (dead token, disconnected, etc — see
// hubspot-architecture.md's Error Handling table) — a request-level try/catch
// alone would call that a "success". `hubspot.connected` is the real signal;
// pull the first panel's real reason (all HubSpot-backed panels fail the same
// underlying way, so any one of them carries the useful message) rather than a
// generic "not connected".
function firstUnavailableReason(snapshot: DashboardSnapshot): string | undefined {
  const panels = [snapshot.today, snapshot.brief, snapshot.thisWeek, snapshot.activity]
  for (const panel of panels) {
    // `restricted` is a permission outcome, not a data failure — it must never be
    // reported as the reason a manual refresh "failed".
    if (panel.status === 'unavailable') return panel.reason
  }
  return undefined
}

export function useDashboard() {
  // Dashboard/Chat/Settings are sibling routes (App.tsx), not nested under a shared
  // layout, so navigating away and back unmounts and remounts this hook from
  // scratch. Seeding from the module-level cache (dashboardSnapshotCache.ts) means a
  // repeat visit renders the last-known snapshot immediately instead of a fresh
  // skeleton — init() below still fetches a live snapshot underneath it.
  const cachedOnMount = getCachedDashboard()
  const [snapshot, setSnapshot] = useState<DashboardSnapshot | null>(cachedOnMount?.snapshot ?? null)
  const [loading, setLoading] = useState(cachedOnMount === null)
  const [error, setError] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  // The caller's role in the workspace the dashboard is showing — drives which
  // panels are rendered at all (DashboardPage's PANEL_DEFS). The backend enforces
  // the same rule independently; this only avoids rendering an empty shell.
  const [role, setRole] = useState<WorkspaceRole | null>(cachedOnMount?.role ?? null)
  // Mirrors workspaceIdRef below as state so callers (e.g. the header search bar)
  // can react to it; the ref remains the source used inside callbacks that don't
  // need a re-render.
  const [workspaceId, setWorkspaceId] = useState<string | null>(cachedOnMount?.workspaceId ?? null)
  // Resolved once in init() below and reused by refresh() — a ref rather than
  // state since it never needs to trigger a re-render on its own.
  const workspaceIdRef = useRef<string | null>(cachedOnMount?.workspaceId ?? null)
  // Mirrors `role` for the same reason workspaceIdRef mirrors workspaceId: cache
  // writes below happen inside callbacks (the SSE handler, refresh) that need the
  // current role without taking a dependency on React state.
  const roleRef = useRef<WorkspaceRole | null>(cachedOnMount?.role ?? null)

  const cacheSnapshot = useCallback((wsId: string, next: DashboardSnapshot) => {
    if (roleRef.current) setCachedDashboard({ workspaceId: wsId, role: roleRef.current, snapshot: next })
  }, [])

  const refresh = useCallback(async (): Promise<DashboardRefreshResult> => {
    const wsId = workspaceIdRef.current
    if (!wsId) return { ok: false, reason: 'Dashboard is still loading — try again in a moment.' }
    setRefreshing(true)
    try {
      const next = await getDashboardSnapshot(wsId)
      setSnapshot(next)
      cacheSnapshot(wsId, next)
      if (!next.hubspot.connected) {
        return { ok: false, reason: firstUnavailableReason(next) ?? 'HubSpot is not connected.' }
      }
      return { ok: true }
    } catch (err) {
      console.error('Manual dashboard refresh failed:', err)
      return { ok: false, reason: err instanceof Error ? err.message : 'Failed to refresh the dashboard.' }
    } finally {
      setRefreshing(false)
    }
  }, [cacheSnapshot])

  useEffect(() => {
    const abort = new AbortController()
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null

    // Keeps reopening the stream whenever it ends or errors, for any reason
    // (network blip, backend restart, browser backgrounding the tab) — without
    // this loop, a single dropped connection silently stops updating until the
    // user manually reloads, which is what was happening before this existed.
    async function streamLoop(wsId: string): Promise<void> {
      while (!abort.signal.aborted) {
        try {
          await streamDashboard(
            wsId,
            (next) => {
              if (abort.signal.aborted) return
              setSnapshot(next)
              cacheSnapshot(wsId, next)
            },
            (message) => { console.error('Dashboard stream error:', message) },
            abort.signal,
          )
        } catch (err) {
          if (abort.signal.aborted) return
          console.error('Dashboard stream dropped, reconnecting…', err)
        }
        if (abort.signal.aborted) return
        await new Promise<void>((resolve) => {
          reconnectTimer = setTimeout(resolve, RECONNECT_DELAY_MS)
        })
      }
    }

    async function init() {
      // A cached snapshot already rendered synchronously from useState's
      // initializer above — only a genuinely first load blanks the screen.
      if (!cachedOnMount) setLoading(true)
      setError('')
      try {
        const workspaces = await getMyWorkspaces()
        if (workspaces.length === 0) {
          setError('No workspace found. Please complete onboarding first.')
          return
        }
        const workspace = workspaces[0]!
        const wsId = workspace.id
        workspaceIdRef.current = wsId
        roleRef.current = workspace.role
        setWorkspaceId(wsId)
        setRole(workspace.role)

        const initial = await getDashboardSnapshot(wsId)
        if (abort.signal.aborted) return
        setSnapshot(initial)
        cacheSnapshot(wsId, initial)

        // Fire-and-forget — errors are logged, not surfaced as a page-level error,
        // since the initial snapshot is already showing and a broken live-update
        // loop is a degraded (not failed) state.
        void streamLoop(wsId)
      } catch (err) {
        if (!abort.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load dashboard')
        }
      } finally {
        if (!abort.signal.aborted) setLoading(false)
      }
    }

    init()

    return () => {
      abort.abort()
      if (reconnectTimer) clearTimeout(reconnectTimer)
    }
    // Mount-once by design (see workspaceIdRef/roleRef above) — cacheSnapshot is
    // stable (useCallback with no deps) and cachedOnMount is only ever read for
    // this same mount's initial decision, so neither belongs in the dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Separate from `refresh`, and separately spinnered, because this is the one
  // action that can cost a model call — the user has to ask for it explicitly.
  const regenerateBrief = useCallback(async (): Promise<DashboardRefreshResult> => {
    const wsId = workspaceIdRef.current
    if (!wsId) return { ok: false, reason: 'Dashboard is still loading — try again in a moment.' }
    setRegenerating(true)
    try {
      const next = await regenerateBriefRequest(wsId)
      setSnapshot(next)
      cacheSnapshot(wsId, next)
      return { ok: true }
    } catch (err) {
      console.error('Brief regeneration failed:', err)
      return { ok: false, reason: err instanceof Error ? err.message : 'Failed to rewrite the brief.' }
    } finally {
      setRegenerating(false)
    }
  }, [cacheSnapshot])

  // Approving here resumes the same conversation the chat would. The snapshot is
  // re-fetched afterwards rather than patched locally: approving runs tool calls
  // that can change several other rows too.
  const resolveApproval = useCallback(
    async (conversationId: string, action: 'approved' | 'rejected'): Promise<DashboardRefreshResult> => {
      try {
        await resolveApprovalRequest(conversationId, action)
        await refresh()
        return { ok: true }
      } catch (err) {
        console.error('Approval from dashboard failed:', err)
        await refresh()
        return { ok: false, reason: err instanceof Error ? err.message : 'Failed to submit your decision.' }
      }
    },
    [refresh],
  )

  return {
    snapshot,
    role,
    workspaceId,
    loading,
    error,
    refresh,
    refreshing,
    regenerateBrief,
    regenerating,
    resolveApproval,
  }
}
